// combatpro/monster/monsterPersistence.js
//
// Combat PRO - Monster Studio persistence API (Stage 58: Persistence & Cloud CRUD).
//
// The backend-communication layer for custom monsters. It owns nothing but the
// HTTP calls to the existing Combat PRO user-monster endpoints and the mapping of
// raw records into studio models (delegated to the Stage 55 engine). It is the
// direct analogue of the Stage 51 spell persistence module, and uses the same
// shared portal axios client (src/api.js: baseURL https://api.visinvoke.com,
// withCredentials cookie auth, no Authorization header).
//
// Existing endpoints reused verbatim (battletracker.html: fetchUserMonsters 3981,
// viStudioCloudSave 10956, viStudioCloudDelete 10969) - no new APIs, no contract
// changes:
//   GET    /v1/battletracker/user/monsters            -> [{ data, ... }, ...]
//   POST   /v1/battletracker/user/monsters            -> upsert by id
//          body { id, name, displayName, data }
//   DELETE /v1/battletracker/user/monsters/{id}        -> remove by id
//
// The GET response is an array of stored records; each record's studio model lives
// on record.data (legacy fetchUserMonsters maps m => m.data). The POST body mirrors
// viStudioCloudSave exactly: id and name both carry the customMonsterId, displayName
// carries the friendly name, and data carries the full studio model.
//
// No React, no DOM, no localStorage. No optional chaining. No em dash.

import API from '../../api';
import {
  normalizeStudioMonster,
  nextMonsterId
} from './monsterStudioEngine';

var USER_MONSTERS_PATH = '/v1/battletracker/user/monsters';

// Raw fetch: returns the array of studio-model payloads (record.data), unnormalized.
export async function fetchUserMonsters() {
  try {
    var res = await API.get(USER_MONSTERS_PATH);
    var data = res ? res.data : null;
    if (!Array.isArray(data)) { return []; }
    return data
      .map(function(rec) { return (rec && rec.data) ? rec.data : rec; })
      .filter(function(d) { return d && typeof d === 'object'; });
  } catch (e) {
    return [];
  }
}

// Load + normalize into studio models (the "load custom pool" operation). Records
// missing an id receive a pool-aware MON-###### fallback, mirroring how the legacy
// viStudioLoadLibrary normalized through viStudioNormalize / viNextMonId.
export async function loadUserMonsters() {
  var raw = await fetchUserMonsters();
  var out = [];
  for (var i = 0; i < raw.length; i++) {
    var fallback = nextMonsterId(out, 0).id;
    out.push(normalizeStudioMonster(raw[i], fallback));
  }
  return out;
}

// Upsert a single monster. Body shape matches viStudioCloudSave. Resolves true on a
// 2xx response (axios rejects non-2xx), false on any failure.
export async function saveUserMonster(monster) {
  try {
    var m = monster || {};
    var body = {
      id: m.customMonsterId,
      name: m.customMonsterId,
      displayName: m.customName || m.name || 'Custom Monster',
      data: m
    };
    await API.post(USER_MONSTERS_PATH, body);
    return true;
  } catch (e) {
    return false;
  }
}

// Delete by id. Resolves true on success, false on failure.
export async function deleteUserMonster(id) {
  try {
    await API.delete(USER_MONSTERS_PATH + '/' + encodeURIComponent(id));
    return true;
  } catch (e) {
    return false;
  }
}
